import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  TextField,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useAuth } from '../contexts/AuthContext';
import { RafflePricesBlock, RaffleSeriesInfo } from '../components/raffle';
import { getAcceptLanguageHeader, useI18n } from '../i18n';

export default function RafflesPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const token = user?.api_token ? `Bearer ${user.api_token}` : '';
  const [tab, setTab] = useState(0);
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(true);
  const [seriesError, setSeriesError] = useState('');
  const [selectedSeriesId, setSelectedSeriesId] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState('');
  const [winner, setWinner] = useState(null);
  const [winnerLoading, setWinnerLoading] = useState(false);
  const [winnerError, setWinnerError] = useState('');
  const [dailyStats, setDailyStats] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(false);
  const [dailyError, setDailyError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchSeries = async () => {
    if (!token) return;
    setSeriesLoading(true);
    setSeriesError('');
    try {
      const res = await fetch('/api/v1/raffles/series/list?limit=50', {
        headers: { Authorization: token, 'Accept-Language': getAcceptLanguageHeader() },
      });
      if (!res.ok) throw new Error(t('Не удалось загрузить серии'));
      const data = await res.json();
      if (data.status !== 'success') throw new Error(data.error || t('Ошибка загрузки серий'));
      const items = Array.isArray(data.data?.items) ? data.data.items : [];
      setSeries(items);
      if (!selectedSeriesId && items.length) {
        setSelectedSeriesId(items.find(item => item.status === 'active')?.id || items[0].id);
      }
    } catch (e) {
      setSeriesError(e.message || t('Ошибка загрузки серий'));
    } finally {
      setSeriesLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchSeries();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchTickets = async (seriesId) => {
    if (!token || !seriesId) return;
    setTicketsLoading(true);
    setTicketsError('');
    try {
      const params = new URLSearchParams();
      params.set('series_id', seriesId);
      params.set('limit', 6000);
      const res = await fetch(`/api/v1/raffles/tickets?${params.toString()}`, {
        headers: { Authorization: token, 'Accept-Language': getAcceptLanguageHeader() },
      });
      if (!res.ok) throw new Error(t('Не удалось загрузить билеты'));
      const data = await res.json();
      if (data.status !== 'success') throw new Error(data.error || t('Ошибка загрузки билетов'));
      setTickets(Array.isArray(data.data?.items) ? data.data.items : []);
    } catch (e) {
      setTickets([]);
      setTicketsError(e.message || t('Ошибка загрузки билетов'));
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedSeriesId) {
      fetchTickets(selectedSeriesId);
      fetchWinner(selectedSeriesId);
      fetchDailyStats(selectedSeriesId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeriesId, token]);

  const fetchWinner = async (seriesId) => {
    if (!token || !seriesId) return;
    setWinnerLoading(true);
    setWinnerError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', 1);
      params.set('series_id', seriesId);
      const res = await fetch(`/api/v1/raffles/winners?${params.toString()}`, {
        headers: { Authorization: token, 'Accept-Language': getAcceptLanguageHeader() },
      });
      if (!res.ok) throw new Error(t('Не удалось загрузить победителя'));
      const data = await res.json();
      if (data.status !== 'success') throw new Error(data.error || t('Ошибка загрузки победителя'));
      const items = Array.isArray(data.data?.items) ? data.data.items : [];
      setWinner(items[0] || null);
    } catch (e) {
      setWinner(null);
      setWinnerError(e.message || t('Ошибка загрузки победителя'));
    } finally {
      setWinnerLoading(false);
    }
  };

  const fetchDailyStats = async (seriesId) => {
    if (!token || !seriesId) return;
    setDailyLoading(true);
    setDailyError('');
    try {
      const params = new URLSearchParams();
      params.set('series_id', seriesId);
      const res = await fetch(`/api/v1/raffles/stats/daily?${params.toString()}`, {
        headers: { Authorization: token, 'Accept-Language': getAcceptLanguageHeader() },
      });
      if (!res.ok) throw new Error(t('Не удалось загрузить статистику по дням'));
      const data = await res.json();
      if (data.status !== 'success') throw new Error(data.error || t('Ошибка загрузки статистики'));
      const items = Array.isArray(data.data?.items) ? data.data.items : [];
      setDailyStats(items);
    } catch (e) {
      setDailyStats([]);
      setDailyError(e.message || t('Ошибка загрузки статистики'));
    } finally {
      setDailyLoading(false);
    }
  };

  const stats = useMemo(() => {
    if (!tickets.length) {
      return {
        total: 0,
        currency: [],
        daily: [],
      };
    }
    const currencyMap = {};
    const dayMap = {};
    tickets.forEach(ticket => {
      const cur = ticket.currency || 'N/A';
      if (!currencyMap[cur]) {
        currencyMap[cur] = { count: 0, revenue: 0 };
      }
      currencyMap[cur].count += 1;
      currencyMap[cur].revenue += Number(ticket.price || 0);

      const date = ticket.purchased_at ? new Date(ticket.purchased_at) : null;
      if (date && !Number.isNaN(date.getTime())) {
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        dayMap[key] = (dayMap[key] || 0) + 1;
      }
    });
    return {
      total: tickets.length,
      currency: Object.entries(currencyMap).map(([cur, data]) => ({
        currency: cur,
        count: data.count,
        revenue: data.revenue,
      })),
      daily: Object.entries(dayMap)
        .map(([day, count]) => ({ day, count }))
        .sort((a, b) => a.day.localeCompare(b.day)),
    };
  }, [tickets]);

  const activeSeries = series.find(s => s.id === selectedSeriesId);
  const dateLocale = lang === 'ru' ? 'ru-RU' : lang === 'tr' ? 'tr-TR' : 'en-US';
  const tabs = useMemo(() => ([
    { label: t('Билеты'), value: 0 },
    { label: t('Статистика'), value: 1 },
    { label: t('Настройки'), value: 2 },
  ]), [t]);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{t('Розыгрыши')}</Typography>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 3 }}>
        {tabs.map(item => (
          <Tab key={item.value} label={item.label} value={item.value} />
        ))}
      </Tabs>

      {tab === 0 && (
        <TicketsTab
          series={series}
          selectedSeriesId={selectedSeriesId}
          onChangeSeries={setSelectedSeriesId}
          loadingSeries={seriesLoading}
          seriesError={seriesError}
          tickets={tickets}
          loadingTickets={ticketsLoading}
          ticketsError={ticketsError}
          onRefresh={() => {
            fetchSeries();
            if (selectedSeriesId) {
              fetchTickets(selectedSeriesId);
              fetchDailyStats(selectedSeriesId);
            }
          }}
          t={t}
          dateLocale={dateLocale}
        />
      )}

      {tab === 1 && (
        <StatsTab
          stats={stats}
          loading={ticketsLoading || seriesLoading}
          series={activeSeries}
          hasData={dailyStats.length > 0}
          seriesList={series}
          selectedSeriesId={selectedSeriesId}
          onChangeSeries={setSelectedSeriesId}
          seriesLoading={seriesLoading}
          seriesError={seriesError}
          dailyStats={dailyStats}
          dailyLoading={dailyLoading}
          dailyError={dailyError}
          winner={winner}
          winnerLoading={winnerLoading}
          winnerError={winnerError}
          onRefresh={() => {
            fetchSeries();
            if (selectedSeriesId) {
              fetchTickets(selectedSeriesId);
              fetchWinner(selectedSeriesId);
              fetchDailyStats(selectedSeriesId);
            }
          }}
          t={t}
          dateLocale={dateLocale}
        />
      )}

      {tab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <RafflePricesBlock />
          <RaffleSeriesInfo />
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function TicketsTab({
  series,
  selectedSeriesId,
  onChangeSeries,
  loadingSeries,
  seriesError,
  tickets,
  loadingTickets,
  ticketsError,
  onRefresh,
  t,
  dateLocale,
}) {
  const [search, setSearch] = useState('');

  const filteredTickets = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return tickets;
    return tickets.filter(ticket =>
      ['ticket_code', 'user_email', 'user_phone', 'status'].some(key => {
        const value = ticket[key];
        return value && String(value).toLowerCase().includes(term);
      })
    );
  }, [search, tickets]);

  const columns = [
    { field: 'ticket_code', headerName: t('Билет'), flex: 1, minWidth: 160 },
    { field: 'user_email', headerName: 'Email', flex: 1, minWidth: 180 },
    { field: 'user_phone', headerName: t('Телефон'), flex: 0.8, minWidth: 140 },
    { field: 'price', headerName: t('Цена'), flex: 0.5, minWidth: 100, },
    { field: 'currency', headerName: t('Валюта'), flex: 0.4, minWidth: 90 },
    { field: 'status', headerName: t('Статус'), flex: 0.5, minWidth: 100 },
    { field: 'purchased_at', headerName: t('Дата покупки'), flex: 0.8, minWidth: 160,
      valueFormatter: (params) => {
        const val = params.value;
        if (!val) return val;
        const d = new Date(val);
        return Number.isNaN(d.getTime()) ? val : d.toLocaleString(dateLocale);
      },
    },
  ];

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <FormControl sx={{ minWidth: 240 }}>
          <InputLabel id="raffle-series-select">{t('Серия')}</InputLabel>
          <Select
            labelId="raffle-series-select"
            label={t('Серия')}
            value={selectedSeriesId ?? ''}
            onChange={e => onChangeSeries(e.target.value)}
            disabled={loadingSeries || series.length === 0}
          >
            {series.map(item => (
              <MenuItem key={item.id} value={item.id}>
                {item.code || `${t('Серия')} #${item.id}`} {item.status === 'active' ? `• ${t('активная')}` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={t('Поиск по билетам')}
          placeholder={t('Билет, email или телефон')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 260 }}
          size="small"
        />
        <Button variant="outlined" onClick={onRefresh} disabled={loadingSeries || loadingTickets}>
          {t('Обновить')}
        </Button>
      </Box>

      {seriesError && <Alert severity="error" sx={{ mb: 2 }}>{seriesError}</Alert>}
      {ticketsError && <Alert severity="error" sx={{ mb: 2 }}>{ticketsError}</Alert>}

      {loadingTickets ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <div style={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={filteredTickets}
            columns={columns}
            getRowId={row => row.id || row.ticket_code}
            disableRowSelectionOnClick
            initialState={{
              sorting: {
                sortModel: [{ field: 'purchased_at', sort: 'desc' }],
              },
            }}
            localeText={{ noRowsLabel: t('Нет билетов для выбранной серии') }}
          />
        </div>
      )}
    </Paper>
  );
}

function StatsTab({
  stats,
  loading,
  series,
  hasData,
  seriesList,
  selectedSeriesId,
  onChangeSeries,
  seriesLoading,
  seriesError,
  dailyStats,
  dailyLoading,
  dailyError,
  winner,
  winnerLoading,
  winnerError,
  onRefresh,
  t,
  dateLocale,
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{t('Кратко')}</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="stats-series-select">{t('Серия')}</InputLabel>
            <Select
              labelId="stats-series-select"
              label={t('Серия')}
              value={selectedSeriesId ?? ''}
              onChange={e => onChangeSeries(e.target.value)}
              disabled={seriesLoading || seriesList.length === 0}
            >
              {seriesList.map(item => (
                <MenuItem key={item.id} value={item.id}>
                  {item.code || `${t('Серия')} #${item.id}`} {item.status === 'active' ? `• ${t('активная')}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
        </Box>
        {seriesError && <Alert severity="error" sx={{ mb: 2 }}>{seriesError}</Alert>}
        {series && (
          <Typography sx={{ mb: 1, color: 'text.secondary' }}>
            {t('Серия')}: <strong>{series.code || series.name}</strong> • {t('Статус')}: {series.status}
          </Typography>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <StatCard title={t('Всего билетов')} value={stats.total} />
            {stats.currency.map(item => (
              <StatCard
                key={item.currency}
                title={`${t('Билетов')} (${item.currency})`}
                value={`${item.count} ${t('шт')} / ${item.revenue.toFixed(2)} ${item.currency}`}
              />
            ))}
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{t('Победитель')}</Typography>
        {winnerError && <Alert severity="error" sx={{ mb: 2 }}>{winnerError}</Alert>}
        {winnerLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : winner ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography><strong>{t('Билет')}:</strong> {winner.ticket_code || '—'}</Typography>
            <Typography><strong>Email:</strong> {winner.user_email || '—'}</Typography>
            <Typography><strong>{t('Телефон')}:</strong> {winner.user_phone || '—'}</Typography>
            <Typography><strong>{t('Приз')}:</strong> {winner.prize || '—'}</Typography>
            <Typography><strong>{t('Дата')}:</strong> {winner.awarded_at ? new Date(winner.awarded_at).toLocaleString(dateLocale) : '—'}</Typography>
          </Box>
        ) : (
          <Typography color="text.secondary">{t('Победитель ещё не определён.')}</Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{t('Продажи по дням')}</Typography>
        {dailyError && <Alert severity="error" sx={{ mb: 2 }}>{dailyError}</Alert>}
        {dailyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : hasData ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {dailyStats.map(item => (
              <Box key={item.day} sx={{ display: 'flex', justifyContent: 'space-between', bgcolor: '#f8f9fb', p: 1.5, borderRadius: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>{item.day}</Typography>
                <Typography>{item.tickets ?? item.count} {t('билетов')}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary">{t('Нет данных для статистики')}</Typography>
        )}
      </Paper>
    </Box>
  );
}

function StatCard({ title, value }) {
  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, minWidth: 220 }}>
      <Typography sx={{ color: 'text.secondary', textTransform: 'uppercase', fontSize: 12 }}>{title}</Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>{value}</Typography>
    </Paper>
  );
}

